// Kyte upstream calls — service_handler wrappers only. Responses are
// normalized through `../normalize` before they leave this module.

import { service_handler } from "@/services";
import {
  normalize,
  split_offers_from_response,
  merge_details_into_raw,
  build_passengers_for_details,
} from "./normalize";
import { build_search_request } from "./request";
import { extract_dictionaries } from "./dictionaries";
import { merge_dictionaries } from "../base/helpers";
import { unwrap } from "./helpers";

/**
 * Parse a pre-fetched Kyte shop-flights response into normalized
 * FlightOffer[]. Used by `search`; exposed for tests / offline fixtures.
 * @param {object} provider
 * @param {object} response
 * @returns {Array}
 */
export const parse_search_response = (provider, response) => {
  const body = unwrap(response);
  merge_dictionaries(provider, extract_dictionaries(body));
  return split_offers_from_response(provider, body).map((raw_offer) =>
    normalize(provider, raw_offer),
  );
};

/**
 * Generic search entry. Translates the booking-engine payload into a Kyte
 * ShopFlightsRequest and returns normalized FlightOffer[]. `modal: false`
 * keeps Kyte search failures silent so the multi-provider results page
 * still surfaces the other providers' offers.
 * @param {object} provider
 * @param {object} payload
 * @returns {Promise<Array>}
 */
export const search = (provider, payload) =>
  service_handler(
    "kyte.search",
    { shopFlightsRequest: build_search_request(payload) },
    { modal: false },
  ).then((response) => parse_search_response(provider, response));

/**
 * POST kyte.details (lfk_details.php). Enriches the offer with disclosures,
 * baggageAllowance, servicingFees and full fare rules; the response merges
 * into the raw payload and re-normalizes as a drop-in replacement.
 * @param {object} provider
 * @param {object} flight_offer
 * @returns {Promise<object>}
 */
export const get_offer_details = (provider, flight_offer) => {
  const body = {
    offerDetailsRequest: {
      offerIds: [flight_offer.providerOfferId],
      passengers: build_passengers_for_details(flight_offer),
    },
  };
  return service_handler("kyte.details", body).then((response) => {
    const envelope = unwrap(response) || {};
    const details_entry =
      envelope.details && envelope.details[flight_offer.providerOfferId]
        ? envelope.details[flight_offer.providerOfferId]
        : null;
    const merged_raw = merge_details_into_raw(flight_offer._raw, details_entry);
    const normalized = normalize(provider, merged_raw);
    if (envelope.possibleActions) {
      const has_bundle_action = Object.keys(envelope.possibleActions).some(
        (possibleAction) => String(possibleAction).split("/").includes("bundles"),
      );
      normalized._possibleActions = { get_upsell: has_bundle_action };
    }
    return normalized;
  });
};
